import React, { Component } from 'react';
import { Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import lodashCompact from 'lodash/fp/compact';
import lodashIdentity from 'lodash/fp/identity';

import getListingFormFields from 'common/listingFormFields';

import {
  getListingFullAddressString,
  isListingFullService,
  isListingSunRise,
  isListingTenantScreening,
} from 'common/listingHelpers';

import {
  hasRoleAdmin,
} from 'common/userRoles';

import {
  formatPrice,
} from 'common/formatters';

import {
  MARKETING_PARTNER_LISTING_STATUS_COMPLETED,
  MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
  MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
  MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
  MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN,
  MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OPTION_ITEMS,
  MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER,
  MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER,
} from 'common/marketingPartnerListingConstants';

import {
  cleanMarketingPartnerListingFormValues,
} from 'common/marketingPartnerListingHelpers';

import {
  HOUSEHOLD_LISTING_STATUS_ACTIVE,
} from 'common/householdConstants';

import {
  LISTING_SERVICE_TYPE_FULL_SERVICE,
  LISTING_SERVICE_TYPE_SUNRISE,
  LISTING_SERVICE_TYPE_TENANT_SCREENING,
  LISTING_STATUS_LEASED,
} from 'common/listingConstants';

import {
  partnerEditableAdditionalListingDetails as partnerEditableAdditionalListingDetailsFieldgroup,
} from 'server/models/Listing/fieldgroups';

import {
  createInitialValues,
  createFormValidationFn,
  createFormFieldsByIdGetter,
  showFieldIf,
  scrollToFormField,
} from 'client/helpers/formikHelpers';

import {
  dataTable,
  actionDataTableReset,
  actionDataTableLoadItems,
  actionDataTableLoadItemsIfNotFullyLoaded,
  actionInitializeDataTableState,
} from 'client/helpers/dataTables';

import withApiState from 'client/helpers/withApiState';

import {
  SCREEN_ID_PARTNER_LISTING_EDIT_PROPERTY_DETAILS,
} from 'client/constants/screenIds';

import {
  SR_API_ACTION_PARTNER_EDIT_LISTING,
} from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
  DATA_TABLE_SOURCE_PARTNER_LISTING_HISTORICAL_UPDATES,
} from 'common/dataTableConstants';

import SrText from 'client/components/SrText';
import SrCard, {
  SR_CARD_BULLET_COLOR_BLUE,
  SR_CARD_CONTENT_TYPE_FORM_REGULAR,
  SR_CARD_CONTENT_TYPE_FORM_GRID,
} from 'client/components/SrCard/SrCard';
import SrFooterButton from 'client/components/SrFooterButton/SrFooterButton';
import SrFormikField, {
  SR_FIELD_THEME_ADOBE,
} from 'client/components/SrFormikField';
import SrBox from 'client/components/SrBox';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrStaticFooter from 'client/components/SrStaticFooter';
import SrGridRow from 'client/components/SrGridRow/SrGridRow';
import SrPartnerPageV2 from 'client/components/SrPartnerPageV2/SrPartnerPageV2';
import SrRouteLeavingGuard from 'client/components/SrRouteLeavingGuard';
import SrAccountManagerContactSupport from 'client/components/SrAccountManagerContactSupport/SrAccountManagerContactSupport';
import SrListingRequestWithdrawnAlertModal from 'client/components/SrListingRequestWithdrawnAlertModal';
import SrListingWithdrawnAlertModal from 'client/components/SrListingWithdrawnAlertModal';

import ListingActions from 'client/actions/Listing';
import PartnerActions from 'client/actions/Partner';
import PartnerUserActions from 'client/actions/PartnerUser';
import AccountManagerActions from 'client/actions/AccountManager';
import postWithState from 'client/actions/postWithState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import branch from 'client/core/branch';
import getMarketingPartnerListingFormFields from 'common/marketingPartnerListingFormFields';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

const FormikContext = React.createContext();

const getServiceType = (listing) => {
  if (isListingTenantScreening(listing)) {
    return LISTING_SERVICE_TYPE_TENANT_SCREENING;
  }
  if (isListingSunRise(listing)) {
    return LISTING_SERVICE_TYPE_SUNRISE;
  }
  return LISTING_SERVICE_TYPE_FULL_SERVICE;
};

const getListingFormikFieldsByService = (listing, formValues) => {
  const service = getServiceType(listing);
  switch (service) {
    case LISTING_SERVICE_TYPE_TENANT_SCREENING: {
      return {
        fieldgroup: [
          'availableDate',
          'price',
        ],
        isRequired: true,
      };
    }
    case LISTING_SERVICE_TYPE_SUNRISE: {
      return {
        fieldgroup: [
          'price',
          'availableDate',
          'description',
          'comparativeMarketAnalysisRentPrice',
          'comparativeMarketAnalysisPdfUrl',
          'comparativeMarketAnalysisNotes',
          'comparativeMarketAnalysisUpdated',
          'brokerPriceOpinionPdfUrl',
          'photoUrls',
          'marketingInfoCollectedFromOwner',
        ],
        updates:{
          comparativeMarketAnalysisRentPrice:{
            validations: formValues.comparativeMarketAnalysisPdfUrl
              ? ['required']
              : [],
          },
          comparativeMarketAnalysisPdfUrl:{
            label: '(.pdf format)',
            properties: {
              fileAccept: '.pdf',
              filePath: `/listing-uploads/comparative-market-analysis/listing-${listing.id}-{uuid}`,
              placeholder: 'Upload CMA Document',
              successMessage: 'File successfully uploaded!',
            },
            validations: formValues.comparativeMarketAnalysisRentPrice
              ? ['required']
              : [],
          },
          brokerPriceOpinionPdfUrl: {
            label: '(.pdf format)',
            properties: {
              fileAccept: '.pdf',
              filePath: `/listing-uploads/broker-price-opinion/listing-${listing.id}-{uuid}`,
              placeholder: 'Upload BPO Document',
              successMessage: 'File successfully uploaded!',
            },
          },
          photoUrls: {
            properties: {
              fileAccept: 'image/*',
              filePath: '/listing-uploads/photos/{uuid}',
              placeholder: 'Upload photos',
              successMessage: 'File successfully uploaded!',
            },
          },
        },
        isRequired: false,
      };
    }
    case LISTING_SERVICE_TYPE_FULL_SERVICE:
    default:
      return {
        fieldgroup: [
          'bedrooms',
          'bathrooms',
          'squareFeet',
          'propertyType',
          'description',
          'showingRemarks',
          'availableDate',
          'securityDeposit',
          'price',
          'yearBuilt',
        ],
        updates: {
          description: {
            subtitle: 'This is posted to the MLS as the Public Remarks.',
          },
          showingRemarks: {
            subtitle: 'This is posted to the MLS as the Private Remarks.',
          },
        },
        isRequired: true,
      };
  }
};

const getMarketingPartnerListingStatusOptionItems = ({
  isAdmin,
  marketingListing,
  shouldChargeWithdrawFee,
}) => {
  const optionItems = [
    {
      label: 'Active',
      value: MARKETING_PARTNER_LISTING_STATUS_COMPLETED,
    },
    {
      label: 'Temporarily Off Market',
      value: MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET,
    },
  ];

  if (!isAdmin) {
    optionItems.push({
      label: shouldChargeWithdrawFee ? 'Request Withdrawal (additional fee)' : 'Request Withdrawn',
      value: MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN,
    });
  }
  else if (marketingListing?.status === MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN) {
    optionItems.push({
      label: shouldChargeWithdrawFee ? 'Request Withdrawal (additional fee)' : 'Request Withdrawn',
      value: MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN,
    });
  }

  if (isAdmin) {
    optionItems.push({
      label: 'Withdrawn',
      value: MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
    });
  }

  return optionItems;
};

const getMarketingPartnerFormikFieldsByService = ({
  isAdmin,
  values,
  listing,
  marketingListing,
  shouldChargeWithdrawFee,
}) => {

  const service = getServiceType(listing);
  switch (service) {
    case LISTING_SERVICE_TYPE_TENANT_SCREENING: {
      return {};
    }
    case LISTING_SERVICE_TYPE_SUNRISE: {
      return {};
    }
    case LISTING_SERVICE_TYPE_FULL_SERVICE:
    default: {
      const shouldShowTemporarilyOffMarketFields = (
        values.status === MARKETING_PARTNER_LISTING_STATUS_TEMPORARILY_OFF_MARKET
        && marketingListing
      );

      const shouldShowWithdrawalReasonField = (
        (values.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN || values.status === MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN) // eslint-disable-line max-len
        && marketingListing
      );

      return {
        fieldgroup: [
          'status',
          'temporarilyOffMarketReason',
          'temporarilyOffMarketReasonProvided',
          'bringBackToMarketDate',
          'withdrawalReason',
          'withdrawalReasonProvided',
        ],
        updates: {
          status: {
            properties: {
              choices: getMarketingPartnerListingStatusOptionItems({
                isAdmin,
                marketingListing,
                shouldChargeWithdrawFee,
              }),
              className: shouldShowWithdrawalReasonField
                ? 'greenBorder'
                : null,
              disabled: listing.status === LISTING_STATUS_LEASED
                || (!isAdmin && marketingListing.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN),
            },
            validations: ['required'],
          },
          temporarilyOffMarketReason:{
            validations: ['required'],
            hideFieldIf: !shouldShowTemporarilyOffMarketFields,
          },
          temporarilyOffMarketReasonProvided:{
            properties: {
              placeholder: 'Write here...',
            },
            label: (
              <span
                className='title'
                style={{ color: '#E7711B', fontWeight: '500' }}
              >
                the owner will see this message
              </span>
            ),
            hideFieldIf: !(
              shouldShowTemporarilyOffMarketFields
              && values.temporarilyOffMarketReason === MARKETING_PARTNER_LISTING_TEMPORARILY_OFF_MARKET_REASON_OTHER // eslint-disable-line max-len
            ),
          },
          bringBackToMarketDate:{
            label: '(Optional)',
            hideFieldIf: !shouldShowTemporarilyOffMarketFields,
          },
          withdrawalReason: {
            properties: {
              choices: MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OPTION_ITEMS,
              disabled: !isAdmin && values.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN,
            },
            validations: ['required'],
            hideFieldIf: !shouldShowWithdrawalReasonField,
          },
          withdrawalReasonProvided: {
            properties: {
              placeholder: 'Write here...',
            },
            validations: ['required'],
            hideFieldIf: !(
              shouldShowWithdrawalReasonField
              && values.withdrawalReason === MARKETING_PARTNER_LISTING_WITHDRAWN_REASON_OTHER
            ),
          },
        },
      };
    }
  }
};

const getPartnerEditableAdditionalListingDetailsFields = (listing) => {
  const service = getServiceType(listing);
  switch (service) {
    case LISTING_SERVICE_TYPE_TENANT_SCREENING:
      return [];
    case LISTING_SERVICE_TYPE_SUNRISE:
      return [];
    case LISTING_SERVICE_TYPE_FULL_SERVICE:
    default:
      return getListingFormFields({
        fieldgroup: partnerEditableAdditionalListingDetailsFieldgroup,
      }).map((formField) => ({
        ...formField,
        title: '',
        type: 'multiple_choice',
        properties: {
          multipleSelection: false,
          useArrayValues: false,
          toggleSingleSelection: true,
          choices: [{
            label: formField.title,
            value: true,
          }],
          noMargin: true,
        },
      }));
  }
};

const getFormSpecificFieldsByService = ({
  listing,
  filteredPartnerUsers,
  isAdmin,
}) => {
  const service = getServiceType(listing);
  switch (service) {
    case LISTING_SERVICE_TYPE_TENANT_SCREENING:
      return [];
    case LISTING_SERVICE_TYPE_SUNRISE:
      return [];
    case LISTING_SERVICE_TYPE_FULL_SERVICE:
    default: {
      return [
        {
          id: 'coListingAgentMlsNumber',
          title: 'Add Co-Listing Agent on the MLS',
          type: 'select',
          properties: {
            choices: [
              {
                label: 'None, Choose Agent',
                value: null,
              },
              ...filteredPartnerUsers.map((partnerUser) => ({
                label: `${partnerUser.user.name} - ${partnerUser.agentMlsNumber}`,
                value: partnerUser.agentMlsNumber,
              })),
            ],
          },
        },
        showFieldIf(filteredPartnerUsers.length > 0 && isAdmin, {
          id: 'buyersAgentCommission',
          title: 'Buyer’s Agent Commission',
          type: 'percentage',
          properties: {
            pattern: '[0-9]*',
            placeholder: '100',
            onlyAcceptNumbers: true,
          },
          validations: ['required'],
        }),
      ];
    }
  }
};

const getFormFields = ({
  values = {},
  listing = {},
  filteredPartnerUsers = [],
  currentUser,
  marketingListing,
  shouldChargeWithdrawFee,
} = {}) => {
  const isAdmin = hasRoleAdmin(currentUser.roles);
  const listingFormikFields = getListingFormikFieldsByService(listing, values);
  const marketingListingFormikFields = getMarketingPartnerFormikFieldsByService({
    isAdmin,
    values,
    listing,
    marketingListing,
    shouldChargeWithdrawFee,
  });

  const listingFields = getListingFormFields({
    fieldgroup: listingFormikFields.fieldgroup,
    updates: listingFormikFields.updates,
    required: listingFormikFields.isRequired,
  });

  const marketingListingFields = getMarketingPartnerListingFormFields({
    fieldgroup: marketingListingFormikFields.fieldgroup,
    updates: marketingListingFormikFields.updates,
    required: true,
  });

  const partnerEditableAdditionalListingDetailsFields = getPartnerEditableAdditionalListingDetailsFields(listing); // eslint-disable-line max-len
  const formSpecificFields = getFormSpecificFieldsByService({
    listing,
    filteredPartnerUsers,
    isAdmin,
  });

  return [
    ...listingFields,
    ...marketingListingFields,
    ...partnerEditableAdditionalListingDetailsFields,
    ...formSpecificFields,
  ];
};

function getListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'listing',
    dataSource: DATA_TABLE_SOURCE_PARTNER_LISTINGS,
    viewId: `by-listingId-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'listings.id': parseInt(listingId, 10),
      'listings.partnerId': partner.id,
    },
    customParams: {
      partnerListings: {},
    },
  };

  return dataTableSettings;
}

function getMarketingListingDataTableSettings({ partner, match }) {
  const listingId = parseInt(match.params.listingId, 10);
  if (!partner) {
    return null;
  }
  const dataTableSettings = {
    itemsPropName: 'marketingListing',
    dataSource: DATA_TABLE_SOURCE_MARKETING_PARTNER_LISTINGS,
    viewId: `partner-id-${partner.id}-listing-id-${listingId}`,
    count: 1,
    injectSingleItem: true,
    filter: {
      'partners.slug': match.params.partnerSlug,
      'marketingPartnerListings.listingId': listingId,
    },
    sort: {
      'marketingPartnerListings.createdDate': 'desc',
    },
  };
  return dataTableSettings;
}

const SR_SUCCESS_MODAL_CONTENT_TYPE_DEFAULT = 'Default';
const SR_SUCCESS_MODAL_CONTENT_TYPE_REQUEST_WITHDRAWN = 'RequestWithdrawn';
const SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_NOT_CHARGED = 'WithdrawnNotCharged';
const SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_CHARGED = 'WithdrawnCharged';

const SrSuccessModal = ({
  partner,
  contentType,
  onClose,
}) => {
  if (contentType === SR_SUCCESS_MODAL_CONTENT_TYPE_REQUEST_WITHDRAWN) {
    return (
      <SrSimpleMessageModal
        theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        iconUrl='/images/checkmarkSuccessGreen.svg'
        headline='Request sent!'
        message='Your account manager will be in touch to process this withdrawal.'
        maxWidth={375}
      />
    );
  }

  if (contentType === SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_NOT_CHARGED) {
    return (
      <SrSimpleMessageModal
        theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        iconUrl='/images/checkmarkSuccessGreen.svg'
        headline='Successfully Withdrawn!'
        message='The partner was not charged.'
        maxWidth={375}
      />
    );
  }

  if (contentType === SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_CHARGED) {
    const withdrawalFeeFormatted = formatPrice(partner.withdrawalFee);

    return (
      <SrSimpleMessageModal
        theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
        iconUrl='/images/checkmarkSuccessGreen.svg'
        headline='Successfully Withdrawn!'
        message={`Partner will be invoiced ${withdrawalFeeFormatted}`}
        maxWidth={375}
      />
    );
  }

  return (
    <SrSimpleMessageModal
      iconUrl='/images/checkmarkSuccessGreen.svg'
      headline={(
        <span>Successfully <br /> edited property!</span>
      )}
      message='Changes are live instantly on Sunroom.'
      buttonText='ok, thanks'
      maxWidth={375}
      onClose={onClose}
    />
  );
};

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNER_LISTING_EDIT_PROPERTY_DETAILS)
@withScrollToTop
@withApiState({
  partnerEditListingApiState: {
    action: SR_API_ACTION_PARTNER_EDIT_LISTING,
  },
})
@branch(({ match }) => ({
  partner: ['partners', 'bySlug', match.params.partnerSlug],
  accountManager: ['accountManagers', 'byListingId', match.params.listingId],
  householdsInterested: ['partners', 'householdsInterestedByListingIds', match.params.listingId],
  partnerUsersByPartnerId: ['partners', 'users', 'listByPartnerId'],
  currentUser: ['currentUser'],
  shouldChargeWithdrawFee: ['listings', 'shouldChargeWithdrawFee', match.params.listingId],
}))
@dataTable(getMarketingListingDataTableSettings)
@dataTable(getListingDataTableSettings)
class PartnerEditListingPropertyDetailsPage extends Component {
  static getPageMetadata = () => ({
    pageTitle: 'Edit Listing | Property Details',
  })

  static fetchData(match, inject) {
    let partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);
    const listingId = parseInt(match.params.listingId, 10);

    return Promise.resolve()
      .then(() => {
        if (!partner) {
          return PartnerActions.loadPartnerBySlug(inject, {
            slug: match.params.partnerSlug,
          });
        }
      })
      .then(() => {
        partner = inject.state.get(['partners', 'bySlug', match.params.partnerSlug]);

        const promises = lodashCompact([
          AccountManagerActions.getByListingId(inject, { listingId }),
          PartnerActions.getHouseholdsInterested(inject, {
            listingId: parseInt(match.params.listingId, 10),
          }),
          PartnerUserActions.getPartnerUserListByPartnerId(inject, {
            partnerId: partner.id,
          }),
          ListingActions.shouldChargeWithdrawFee(inject, { listingId, reload: true }),
        ]);

        [
          getListingDataTableSettings({ partner, match }),
          getMarketingListingDataTableSettings({ partner, match }),
        ]
          .filter(lodashIdentity)
          .forEach((dataTableSettings) => {
            actionInitializeDataTableState(inject, dataTableSettings);
            promises.push(actionDataTableLoadItemsIfNotFullyLoaded(inject, dataTableSettings));
          });

        return Promise.all(promises);
      });
  }

  static propTypes = {
    listing: PropTypes.object,
    marketingListing: PropTypes.object,
    partner: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    householdsInterested: PropTypes.array,
    accountManager: PropTypes.object,
    currentUser: PropTypes.object.isRequired,
    partnerEditListingApiState: PropTypes.object.isRequired,
    shouldChargeWithdrawFee: PropTypes.bool,
  }

  constructor(props) {
    super(props);
    this.state = {
      formValuesForSubmission: null,
      editSuccessModalContentType: null,
      warningModalIsOpen: false,
      withdrawnModalIsOpen: false,
      requestWithdrawnAlerModalIsOpen: false,
    };
  }

  componentDidMount() {
    if (process.browser) {
      global.addEventListener('beforeunload', this._onBeforeUnload, true);
    }
    this._scrollToCMA();
    this._scrollToPhotos();
    this._scrollToBPO();
  }

  componentWillUnmount() {
    if (process.browser) {
      global.removeEventListener('beforeunload', this._onBeforeUnload, true);
    }
  }

  componentDidUpdate() {
    this._scrollToCMA();
    this._scrollToPhotos();
    this._scrollToBPO();
  }

  _scrollToCMA = () => {
    const { location } = this.props;
    if (location.search.includes('scrollTo=CMA') && this.scrollHereForCMA) {
      this.scrollHereForCMA.scrollIntoView({ behavior: 'smooth' });
    }
  }

  _scrollToPhotos = () => {
    const { location } = this.props;
    if (location.search.includes('scrollTo=photos') && this.scrollHereForPhotos) {
      this.scrollHereForPhotos.scrollIntoView({ behavior: 'smooth' });
    }
  }

  _scrollToBPO = () => {
    const { location } = this.props;
    if (location.search.includes('scrollTo=BPO') && this.scrollHereForBPO) {
      this.scrollHereForBPO.scrollIntoView({ behavior: 'smooth' });
    }
  }

  _onBeforeUnload = (event) => {
    if (this.dirty) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  _onValidateForm = () => {
    const {
      listing,
      marketingListing,
      currentUser,
    } = this.props;
    const formikBag = this.formik.getFormikBag();
    const formValues = formikBag.values;
    const formFields = getFormFields({
      values: formikBag.values,
      marketingListing: marketingListing,
      listing: listing,
      currentUser: currentUser,
    });
    if (formValues.comparativeMarketAnalysisPdfUrl) {
      formikBag.setFieldValue('comparativeMarketAnalysisUpdated', momentWithAustinTimezone().toISOString());
    }
    const formErrors = createFormValidationFn(formFields)(formValues);
    const formFielIdsWithErrors = Object.keys(formErrors);

    if (!formFielIdsWithErrors.length) {
      formikBag.handleSubmit();
    }
    else {
      formFielIdsWithErrors.forEach((formFieldId) => {
        formikBag.setFieldTouched(formFieldId, true, true);
      });
      scrollToFormField({
        formFieldId: formFielIdsWithErrors[0],
      });
    }
  }

  _onSubmit = (formValues) => {
    const { listing, householdsInterested } = this.props;

    const activeHouseholdsInterested = householdsInterested.filter(({ householdListings }) => {
      const householdListingMatch = householdListings.filter((householdListing) => {
        return householdListing.listingId === listing.id;
      }).pop();

      return (
        householdListingMatch
        && householdListingMatch.status === HOUSEHOLD_LISTING_STATUS_ACTIVE
      );
    });

    const shouldShowWarningModal = (
      activeHouseholdsInterested.length > 0
      && (
        formValues.price > listing.price
        || formValues.securityDeposit > listing.securityDeposit
      )
    );
    this.setState({
      formValuesForSubmission: cleanMarketingPartnerListingFormValues(formValues),
      warningModalIsOpen: shouldShowWarningModal,
    });

    if (shouldShowWarningModal) {
      return;
    }

    this._checkIfShouldStartWithdrawFlow();
  }

  _checkIfShouldStartWithdrawFlow = () => {
    const {
      marketingListing,
      shouldChargeWithdrawFee,
    } = this.props;

    const {
      formValuesForSubmission,
    } = this.state;

    if (
      marketingListing.status !== MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN
      && formValuesForSubmission.status === MARKETING_PARTNER_LISTING_STATUS_REQUEST_WITHDRAWN
      && shouldChargeWithdrawFee
    ) {
      this.setState({
        requestWithdrawnAlerModalIsOpen: true,
      });
      return;
    }

    if (
      marketingListing.status !== MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN
      && formValuesForSubmission.status === MARKETING_PARTNER_LISTING_STATUS_WITHDRAWN
      && shouldChargeWithdrawFee
    ) {
      this.setState({
        withdrawnModalIsOpen: true,
      });
      return;
    }

    this._postFormPayload();
  }

  _postFormPayload = ({
    chargeWithdrawFee,
    editSuccessModalContentType = SR_SUCCESS_MODAL_CONTENT_TYPE_DEFAULT,
  } = {}) => {
    const {
      partner,
      listing,
      dispatch,
    } = this.props;

    const {
      formValuesForSubmission,
    } = this.state;

    dispatch(postWithState, {
      action: SR_API_ACTION_PARTNER_EDIT_LISTING,
      payload: {
        partnerId: partner.id,
        listingId: listing.id,
        chargeWithdrawFee,
        updates: {
          ...formValuesForSubmission,
        },
      },
      onSuccess: () => {
        this.setState({
          editSuccessModalContentType,
          warningModalIsOpen: false,
          withdrawnModalIsOpen: false,
          requestWithdrawnAlerModalIsOpen: false,
        });
        this._reloadPartnerListing();

        if (
          editSuccessModalContentType === SR_SUCCESS_MODAL_CONTENT_TYPE_REQUEST_WITHDRAWN
          || editSuccessModalContentType === SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_CHARGED
          || editSuccessModalContentType === SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_NOT_CHARGED
        ) {
          setTimeout(() => {
            this._onCloseEditSuccessModal();
          }, 3000);
        }
      },
    });
  }

  _onCloseWarningModal = () => {
    this.setState({
      warningModalIsOpen: false,
    });
  }

  _onCloseEditSuccessModal = () => {
    this.setState({
      editSuccessModalContentType: null,
    });
  }

  _reloadPartnerListing = () => {
    const { partner, match, dispatch } = this.props;

    return Promise.all([
      dispatch(actionDataTableLoadItems, getListingDataTableSettings({
        partner,
        match,
      })),
      dispatch(actionDataTableLoadItems, getMarketingListingDataTableSettings({
        partner,
        match,
      })),
      dispatch(actionDataTableReset, {
        dataSource: DATA_TABLE_SOURCE_PARTNER_LISTING_HISTORICAL_UPDATES,
      }),
    ]);
  }

  _getMarketingContentTenantScreening = ({ formFields, formikBag }) => {
    const getFormFieldsById = createFormFieldsByIdGetter(formFields);
    return (
      <SrGridRow autofit autofitMinColumnWidth='185px'>
        <SrFormikField
          formField={getFormFieldsById('price')}
          formikBag={formikBag}
          theme={SR_FIELD_THEME_ADOBE}
        />
        <SrFormikField
          formField={getFormFieldsById('availableDate')}
          formikBag={formikBag}
          theme={SR_FIELD_THEME_ADOBE}
        />
      </SrGridRow>
    );
  }

  _getMarketingContentSunRise = ({ formFields, formikBag }) => {
    const getFormFieldsById = createFormFieldsByIdGetter(formFields);
    return (
      <>
        <SrGridRow autofit autofitMinColumnWidth='185px'>
          <SrFormikField
            formField={getFormFieldsById('price')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
          <SrFormikField
            formField={getFormFieldsById('availableDate')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
        </SrGridRow>
        <SrGridRow autofill>
          <SrFormikField
            formField={getFormFieldsById('description')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
        </SrGridRow>
      </>
    );
  }

  _getMarketingContentFullService = ({ formFields, formikBag }) => {
    const getFormFieldsById = createFormFieldsByIdGetter(formFields);
    const optionallyHiddenFields = [
      getFormFieldsById('temporarilyOffMarketReason'),
      getFormFieldsById('temporarilyOffMarketReasonProvided'),
      getFormFieldsById('bringBackToMarketDate'),
      getFormFieldsById('withdrawalReason'),
      getFormFieldsById('withdrawalReasonProvided'),
    ].filter(({ type }) => type !== 'hidden');

    const optionalFieldsExist = optionallyHiddenFields.length > 0;

    return (
      <>
        {optionalFieldsExist && (
          <SrGridRow autofill autofitMinColumnWidth='185px'>
            <SrFormikField
              formField={getFormFieldsById('status')}
              formikBag={formikBag}
              theme={SR_FIELD_THEME_ADOBE}
            />
            {optionallyHiddenFields.map((formField) => (
              <SrFormikField
                key={formField.id}
                formField={getFormFieldsById(formField.id)}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />
            ))}
          </SrGridRow>
        )}
        <SrGridRow autofit autofitMinColumnWidth='185px'>
          {!optionalFieldsExist &&
              (<SrFormikField
                formField={getFormFieldsById('status')}
                formikBag={formikBag}
                theme={SR_FIELD_THEME_ADOBE}
              />)
          }
          <SrFormikField
            formField={getFormFieldsById('price')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
          <SrFormikField
            formField={getFormFieldsById('securityDeposit')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
          <SrFormikField
            formField={getFormFieldsById('availableDate')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
        </SrGridRow>
        <SrGridRow autofit autofitMinColumnWidth='405px'>
          <SrFormikField
            formField={getFormFieldsById('description')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
          <SrFormikField
            formField={getFormFieldsById('showingRemarks')}
            formikBag={formikBag}
            theme={SR_FIELD_THEME_ADOBE}
          />
        </SrGridRow>
      </>
    );
  }

  _getMarketingSectionContent = ({ formFields, formikBag }) => {
    const { listing } = this.props;
    if (isListingTenantScreening(listing)) {
      return this._getMarketingContentTenantScreening({ formFields, formikBag });
    }
    if (isListingSunRise(listing)) {
      return this._getMarketingContentSunRise({ formFields, formikBag });
    }
    return this._getMarketingContentFullService({ formFields, formikBag });
  }

  _shouldDisplayBasicFeaturesSection = () => {
    const { listing } = this.props;
    const isFullService = isListingFullService(listing);
    return isFullService;
  }

  _shouldDisplayAmenitiesSection = () => {
    const { listing } = this.props;
    const isFullService = isListingFullService(listing);
    return isFullService;
  }

  _shouldDisplayCoListingSection = (filteredPartnerUsers) => {
    const { listing } = this.props;
    const isFullService = isListingFullService(listing);
    return filteredPartnerUsers.length > 0 && isFullService;
  }

  _shouldDisplayAccountManagerSection = () => {
    const { accountManager, listing } = this.props;
    const isFullService = isListingFullService(listing);
    return accountManager && isFullService;
  }

  _shouldDisplayCMASection = () => {
    const { listing } = this.props;
    const isSunRise = isListingSunRise(listing);
    return isSunRise;
  }

  _shouldDisplayBPOSection = () => {
    const { listing } = this.props;
    const isSunRise = isListingSunRise(listing);
    return isSunRise;
  }

  _shouldDisplayPhotosSection = () => {
    const { listing } = this.props;
    const isSunRise = isListingSunRise(listing);
    return isSunRise;
  }

  _shouldDisplayExtraInfoNeededSection = () => {
    const { listing, marketingListing } = this.props;
    const isSunRise = isListingSunRise(listing);
    return isSunRise && marketingListing?.marketingInfoRequiredFromOwner;
  }

  _getAmenitiesFields = () => {
    const { listing } = this.props;
    const isFullService = isListingFullService(listing);
    return isFullService ? partnerEditableAdditionalListingDetailsFieldgroup : [];
  }

  render() {
    const {
      listing,
      partner,
      marketingListing,
      accountManager,
      currentUser,
      partnerEditListingApiState,
      partnerUsersByPartnerId,
      dispatch,
      shouldChargeWithdrawFee,
    } = this.props;

    const {
      editSuccessModalContentType,
      warningModalIsOpen,
      requestWithdrawnAlerModalIsOpen,
      withdrawnModalIsOpen,
    } = this.state;

    if (!partner || !listing | !marketingListing) {
      return null;
    }

    const filteredPartnerUsers = (partnerUsersByPartnerId[partner.id] || []).filter(
      (x) => x.agentMlsNumber
    );

    const marketingListingStatusOptionItemExists = !!getMarketingPartnerListingStatusOptionItems({
      isAdmin: hasRoleAdmin(currentUser.roles),
      marketingListing,
      shouldChargeWithdrawFee,
    }).find((optionItem) => {
      return marketingListing && optionItem.value === marketingListing.status;
    });

    const defaultValues = {
      ...listing,
      status: marketingListingStatusOptionItemExists
        ? marketingListing.status
        : MARKETING_PARTNER_LISTING_STATUS_IN_PROGRESS,
      temporarilyOffMarketReason: marketingListing?.temporarilyOffMarketReason,
      temporarilyOffMarketReasonProvided: marketingListing?.temporarilyOffMarketReasonProvided,
      bringBackToMarketDate: marketingListing?.bringBackToMarketDate,
      withdrawalReason: marketingListing?.withdrawalReason,
      withdrawalReasonProvided: marketingListing?.withdrawalReasonProvided,
      coListingAgentMlsNumber: listing.coListingAgentMlsNumber || partner.coListingAgentMlsNumber,
      marketingInfoCollectedFromOwner: marketingListing?.marketingInfoCollectedFromOwner,
    };

    const shouldDisplayBasicFeaturesSection = this._shouldDisplayBasicFeaturesSection();
    const shouldDisplayAmenitiesSection = this._shouldDisplayAmenitiesSection();
    const shouldDisplayCoListingSection = this._shouldDisplayCoListingSection(filteredPartnerUsers);
    const shouldDisplayAccountManagerSection = this._shouldDisplayAccountManagerSection();
    const shouldDisplayCMASection = this._shouldDisplayCMASection();
    const shouldDisplayBPOSection = this._shouldDisplayBPOSection();
    const shouldDisplayPhotosSection = this._shouldDisplayPhotosSection();
    const shouldDisplayExtraInfoNeededSection = this._shouldDisplayExtraInfoNeededSection();
    const partnerEditableAdditionalListingDetails = this._getAmenitiesFields();
    const isFullService = isListingFullService(listing);
    const isSunRise = isListingSunRise(listing);

    return (
      <SrPartnerPageV2
        className='SrPartnerEditListingPropertyDetailsPage'
        backUrl={`/partner/${partner.slug}/listing/${listing.id}/edit`}
        navbarTitle={listing ? getListingFullAddressString(listing) : null}
        pageHeader='Listing &amp; Marketing'
      >
        <Formik
          ref={(formik) => {
            this.formik = formik;
          }}
          initialValues={createInitialValues({
            formFields: getFormFields({
              values: {},
              marketingListing,
              listing,
              filteredPartnerUsers,
              currentUser,
            }),
            defaultValues: defaultValues,
          })}
          enableReinitialize
          validate={(values) => {
            const formFields = getFormFields({
              values,
              marketingListing,
              listing,
              filteredPartnerUsers,
              currentUser,
            });
            return createFormValidationFn(formFields)(values);
          }}
          onSubmit={this._onSubmit}
          render={(formikBag) => {
            const formFields = getFormFields({
              values: formikBag.values,
              marketingListing,
              listing,
              filteredPartnerUsers,
              currentUser,
              shouldChargeWithdrawFee,
            });
            const getFormFieldsById = createFormFieldsByIdGetter(formFields);
            const marketingSectionContent = this._getMarketingSectionContent({ formFields, formikBag });
            this.dirty = formikBag.dirty;

            return (
              <>
                <FormikContext.Provider value={formikBag}>
                  <Form className='marketProperty'>
                    <SrCard
                      color={SR_CARD_BULLET_COLOR_BLUE}
                      contentType={SR_CARD_CONTENT_TYPE_FORM_REGULAR}
                      header='Marketing'
                      subheader={
                        !isSunRise
                          ? 'Renters see this information on Sunroom and all syndicated websites'
                          : ''
                      }
                    >
                      {marketingSectionContent}
                    </SrCard>

                    { shouldDisplayBasicFeaturesSection && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='Basic Features'
                      >
                        <SrGridRow autofit autofitMinColumnWidth='185px'>
                          <SrFormikField
                            formField={getFormFieldsById('bedrooms')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('bathrooms')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('squareFeet')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('propertyType')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('yearBuilt')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayAmenitiesSection && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='Amenities'
                      >
                        <SrBox marginTop='12px'>
                          <SrGridRow autofit autofitMinColumnWidth='255px'>
                            {partnerEditableAdditionalListingDetails.map((amenityId) => {
                              const formField = getFormFieldsById(amenityId);
                              return (
                                <SrFormikField
                                  key={amenityId}
                                  formField={formField}
                                  formikBag={formikBag}
                                  theme={SR_FIELD_THEME_ADOBE}
                                  margin='0px'
                                />
                              );
                            })}
                          </SrGridRow>
                        </SrBox>
                      </SrCard>
                    )}

                    {shouldDisplayCoListingSection && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='Co-Listing Agent'
                      >
                        <SrGridRow autofit autofitMinColumnWidth='405px'>
                          <SrFormikField
                            formField={getFormFieldsById('coListingAgentMlsNumber')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('buyersAgentCommission')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayCMASection && (
                      <SrCard
                        ref={(el) => { this.scrollHereForCMA = el; }}
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='CMA'
                      >
                        <SrGridRow autofit autofitMinColumnWidth='405px'>
                          <SrFormikField
                            formField={getFormFieldsById('comparativeMarketAnalysisRentPrice')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                        <SrGridRow autofit autofitMinColumnWidth='405px'>
                          <SrFormikField
                            formField={getFormFieldsById('comparativeMarketAnalysisPdfUrl')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={getFormFieldsById('comparativeMarketAnalysisNotes')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayBPOSection && (
                      <SrCard
                        ref={(el) => { this.scrollHereForBPO = el; }}
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='BPO'
                      >
                        <SrGridRow autofit autofitMinColumnWidth='405px'>
                          <SrFormikField
                            formField={getFormFieldsById('brokerPriceOpinionPdfUrl')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayPhotosSection && (
                      <SrCard
                        ref={(el) => { this.scrollHereForPhotos = el; }}
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='Photos'
                      >
                        <SrGridRow autofill>
                          <SrFormikField
                            formField={getFormFieldsById('photoUrls')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayExtraInfoNeededSection && (
                      <SrCard
                        color={SR_CARD_BULLET_COLOR_BLUE}
                        contentType={SR_CARD_CONTENT_TYPE_FORM_GRID}
                        header='Extra Marketing Info Needed'
                      >
                        <SrBox height={14} />
                        <SrText
                          color='grey'
                          font='greycliff'
                          weight='800'
                          size='18'
                          spacing='0'
                          lineHeight='24px'
                        >
                          Partner asked for Sunroom to collect this info from the owner:
                        </SrText>
                        <SrGridRow autofill>
                          <SrText
                            font='greycliff'
                            weight='800'
                            size='20'
                            lineHeight='24px'
                          >
                            {marketingListing.marketingInfoRequiredFromOwner}
                          </SrText>
                          <SrGridRow autofill />
                          <SrFormikField
                            formField={getFormFieldsById('marketingInfoCollectedFromOwner')}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </SrGridRow>
                      </SrCard>
                    )}

                    {shouldDisplayAccountManagerSection && (
                      <SrAccountManagerContactSupport
                        accountManager={accountManager}
                      />
                    )}

                    <SrStaticFooter>
                      <SrText
                        display='block'
                        lineHeight='25px'
                        font='greycliff'
                        color='grey600'
                        fontSize='13px'
                        weight='500'
                      >
                        {isFullService && 'Updates can take 24 hours to syndicate'}
                      </SrText>
                      <SrFooterButton
                        hasBoxShadow={!partnerEditListingApiState.inProgress}
                        disabled={partnerEditListingApiState.inProgress}
                        onClick={this._onValidateForm}
                      >
                        {partnerEditListingApiState.inProgress ? 'Saving...' : 'Save'}
                      </SrFooterButton>
                    </SrStaticFooter>
                  </Form>
                </FormikContext.Provider>
                <SrRouteLeavingGuard
                  when={formikBag.dirty}
                  dispatch={dispatch}
                />
              </>
            );
          }}
        />
        {warningModalIsOpen && (
          <SrSimpleMessageModal
            iconUrl='/images/warning-triangle-orange-large.svg'
            headline='Renters are actively applying'
            message={
              <span>
                {
                  'Are you sure you want to increase the rent while renters ' +
                  'are actively applying? They may get surprised and request a ' +
                  'refund that Sunroom is not responsible for completing. You ' +
                  'are responsible for communicating this change to the renters.'
                }
              </span>
            }
            buttonText='i understand'
            subLabelText='Cancel'
            onSubLabelClick={this._onCloseWarningModal}
            maxWidth={375}
            onClose={this._postFormPayload}
          />
        )}
        {editSuccessModalContentType && (
          <SrSuccessModal
            partner={partner}
            contentType={editSuccessModalContentType}
            onClose={this._onCloseEditSuccessModal}
          />
        )}
        {withdrawnModalIsOpen && (
          <SrListingWithdrawnAlertModal
            partner={partner}
            onSubmit={({ chargeWithdrawFee }) => {
              this._postFormPayload({
                chargeWithdrawFee,
                editSuccessModalContentType: chargeWithdrawFee
                  ? SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_CHARGED
                  : SR_SUCCESS_MODAL_CONTENT_TYPE_WITHDRAWN_NOT_CHARGED,
              });
            }}
            onClose={() => {
              this.setState({
                withdrawnModalIsOpen: false,
              });
            }}
          />
        )}
        {requestWithdrawnAlerModalIsOpen && (
          <SrListingRequestWithdrawnAlertModal
            partner={partner}
            onSubmit={() => {
              this._postFormPayload({
                editSuccessModalContentType: SR_SUCCESS_MODAL_CONTENT_TYPE_REQUEST_WITHDRAWN,
              });
            }}
            onClose={() => {
              this.setState({
                requestWithdrawnAlerModalIsOpen: false,
              });
            }}
          />
        )}
      </SrPartnerPageV2>
    );
  }
}

PartnerEditListingPropertyDetailsPage.displayName = 'PartnerEditListingPropertyDetailsPage';

export default PartnerEditListingPropertyDetailsPage;
